/**
 * New Highs / New Lows — NYSE and NASDAQ side by side, NH / NL / NH−NL each.
 * *Short* is 1D 2D 5D 10D 1M, *Long* 1 Month to 12 Month; every window is QV's
 * rule (decision 7). NH−NL is coloured by its sign.
 */
import type { CSSProperties } from "react";
import { fetchBriefNhnl, type NhnlList, type NhnlPayload, type NhnlWindow } from "../../api/daily-brief";
import { WIDGET_META } from "../../lib/brief-board";
import { Chip, TABLE, TD, TD_NUM, TH, TH_NUM, signColor } from "./cells";
import { useBriefData } from "./useBriefData";
import { DataState, WidgetCard, useWidgetSetting, type WidgetProps } from "./WidgetCard";

const LISTS = ["NYSE", "NASDAQ"] as const;
type Mode = "short" | "long";
/** The server's window codes, spelled out on the Long rows as the mock has them; Short keeps the codes. */
const LONG_LABEL: Record<string, string> = { "1M": "1 Month", "3M": "3 Month", "6M": "6 Month", "12M": "12 Month" };

export function NhNlWidget({ widget, onSettings, onRemove }: WidgetProps) {
  const [mode, setMode] = useWidgetSetting<Mode>(widget, "mode", "short", onSettings);
  const state = useBriefData<NhnlPayload>("nhnl", fetchBriefNhnl);
  return (
    <WidgetCard
      title={WIDGET_META[widget.type].title}
      live={state.data?.live}
      onRemove={onRemove}
      controls={
        <>
          <Chip on={mode === "short"} onClick={() => setMode("short")}>Short</Chip>
          <Chip on={mode === "long"} onClick={() => setMode("long")}>Long</Chip>
        </>
      }
    >
      <DataState state={state}>{(data) => <NhNlTable data={data} mode={mode} />}</DataState>
    </WidgetCard>
  );
}

function NhNlTable({ data, mode }: { data: NhnlPayload; mode: Mode }) {
  const lists = LISTS.map((code) => data.lists[code] ?? null);
  const windowsOf = (l: NhnlList | null): NhnlWindow[] => (l ? l[mode] : []);
  // The windows in the server's order, from whichever list has them.
  const labels: string[] = [];
  for (const l of lists) for (const w of windowsOf(l)) if (!labels.includes(w.window)) labels.push(w.window);
  const at = (l: NhnlList | null, label: string) => windowsOf(l).find((w) => w.window === label);

  return (
    <table style={TABLE}>
      <thead>
        <tr>
          <th style={TH} />
          {LISTS.map((code) => (
            <th key={code} colSpan={3} style={{ ...TH, ...SEP, textAlign: "center" }}>{code}</th>
          ))}
        </tr>
        <tr>
          <th style={TH}>{mode === "short" ? "Window" : "Time"}</th>
          {LISTS.map((code) => [
            <th key={`${code}nh`} style={{ ...TH_NUM, ...SEP }}>NH</th>,
            <th key={`${code}nl`} style={TH_NUM}>NL</th>,
            <th key={`${code}d`} style={TH_NUM}>NH−NL</th>,
          ])}
        </tr>
      </thead>
      <tbody>
        {labels.map((label) => (
          <tr key={label}>
            <td style={TD}>{mode === "long" ? (LONG_LABEL[label] ?? label) : label}</td>
            {lists.map((l, i) => {
              const w = at(l, label);
              const d = w ? w.nh - w.nl : null;
              return [
                <td key={`${i}nh`} style={{ ...TD_NUM, ...SEP }}>{w?.nh ?? "—"}</td>,
                <td key={`${i}nl`} style={TD_NUM}>{w?.nl ?? "—"}</td>,
                <td key={`${i}d`} style={{ ...TD_NUM, color: signColor(d) }}>{d ?? "—"}</td>,
              ];
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const SEP: CSSProperties = { borderLeft: "2px solid var(--qf-border)" };
